import React, { ReactElement, Ref, useContext, useRef } from 'react'
import { GenericFooter } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import Heading, { HeadingLevel } from '../../Common/Components/Heading'
import Icon, { IconName } from '../../Common/Components/Icon'
import { IconSize } from '../../../NonReact/constants/React/Icon/Constants'
import ModalExitPromptContext, { ModalExitPromptType } from '../../Common/Components/Modal/ModalExitPromptContext'
import P from '../../Common/Components/P'
import PopperBubble from '../../Common/Components/PopperBubble'
import { DocTopic } from '../../Common/Constants/Documentation'
import { LanguageContext } from '../../Common/Contexts'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import { openHelpPortalByTopic } from '../../Common/Utils'
import { RoundedButton } from '../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { servletPath } from '../../../NonReact/common/RxJS/RxJS'

/**
 * Component properties type.
 */
type Props = {
    isValidationPopperOpen: boolean
    closeModal: () => void
    serverError?: string
}

/**
 * Footer actions for the Add User modal.
 *
 * @param isValidationPopperOpen - Is the validation popper open.
 * @param closeModal - Close modal function.
 * @param serverError Server error string.
 */
const FooterActions: React.FC<Props> = ({ isValidationPopperOpen, closeModal, serverError }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    language.loadBundle(GenericFooter.NAME)

    const elementRef: Ref<HTMLButtonElement> = useRef(null)

    const handleHelpClick = (): void => {
        openHelpPortalByTopic(servletPath, DocTopic.ADMINCALLRECINT)
    }

    const handleCancel = (): void => {
        if (commonForm?.isTouched) {
            displayExitPrompt()
        } else {
            closeModal()
        }
    }

    return (
        <div>
            {serverError && (
                <div className='auf-server-error'>
                    <Icon iconName={IconName.ERRORCIRCLE} iconSize={IconSize.LARGE} />
                    <div>
                        <Heading level={HeadingLevel.TWO}>
                            {language.get(GenericFooter.GenericFooter_ServerErrorTitle)}
                        </Heading>
                        <P>{serverError}</P>
                    </div>
                </div>
            )}
            <div className='tabs-footer'>
                <RoundedButton type='button' onClick={handleHelpClick} shrink>
                    {language.get(GenericFooter.GenericFooter_ButtonHelpLabel)}
                </RoundedButton>
                <RoundedButton popoverRef={elementRef} type='submit' shrink>
                    {language.get(GenericFooter.GenericFooter_ButtonSubmitLabel)}
                </RoundedButton>

                <PopperBubble
                    anchorEl={elementRef.current}
                    open={isValidationPopperOpen}
                    placement='top'
                    content={
                        <div className='validation-popper-container'>
                            <Icon iconName={IconName.ERRORCIRCLE} />
                            <span>{language.get(GenericFooter.GenericFooter_ValidationWarning)}</span>
                        </div>
                    }
                    className='colour-red'
                />

                <RoundedButton onClick={handleCancel} isOutlined={true} type='button' shrink>
                    {language.get(GenericFooter.GenericFooter_ButtonCancelLabel)}
                </RoundedButton>
            </div>
        </div>
    )
}

export default FooterActions
